import {
    GeoJSONSource,
    GeoJSONSourceSpecification,
    LayerSpecification,
    Map,
} from "mapbox-gl";
import * as turf from "@turf/turf";
import { Feature, LineString, MultiPolygon, Point } from "geojson";

export function addUpdateGeojsonSource(
    map: Map,
    source: string,
    data: GeoJSONSourceSpecification,
) {
    if (!map) return;
    const s = map.getSource(source) as GeoJSONSource;
    if (s) {
        if (data.data) {
            s.setData(data.data);
        }
    } else {
        map.addSource(source, data);
    }
}

export function addUpdateGeojsonLayers(map: Map, layers: LayerSpecification[]) {
    if (!map) return;
    layers.forEach((layer) => {
        const l = map.getLayer(layer.id);
        if (!l) {
            map.addLayer(layer);
        }
    });
}

export function removeLayersIfExists(map: Map, layerIds: string[]) {
    if (!map) return;
    layerIds?.forEach((layerId) => {
        const l = map.getLayer(layerId);
        if (l) {
            map.removeLayer(layerId);
        }
    });
}

export function removeSourcesIfExists(map: Map, sourceIds: string[]) {
    if (!map) return;
    sourceIds?.forEach((sourceId) => {
        const s = map.getSource(sourceId) as GeoJSONSource;
        if (s) {
            map.removeSource(sourceId);
        }
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function invertedTurfBboxToFourCornerCoordinates(boundingBox: any) {
    if (!boundingBox || boundingBox.length !== 4) return null;
    const [minX, minY, maxX, maxY] = boundingBox;
    const topLeft = [minX, maxY];
    const topRight = [maxX, maxY];
    const bottomRight = [maxX, minY];
    const bottomLeft = [minX, minY];

    return [bottomLeft, bottomRight, topRight, topLeft];
}

export function updateLayerOpacity(
    map: Map,
    layerIds: string[],
    opacity: number,
) {
    layerIds.forEach((layerId) => {
        const layer = map.getLayer(layerId);
        if (layer) {
            if (["fill", "line", "raster"].includes(layer.type)) {
                const paintProperty = `${layer.type}-opacity`;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                map.setPaintProperty(layerId, paintProperty as any, opacity);
            }
        }
    });
}

export function updateLayerVisibility(
    map: Map,
    layerIds: string[],
    visibility: boolean,
) {
    if (!map) return;
    layerIds.forEach((layerId) => {
        const layer = map.getLayer(layerId);
        if (layer) {
            map.setLayoutProperty(
                layerId,
                "visibility",
                visibility ? "visible" : "none",
            );
        }
    });
}

export function moveLayersToTop(map: Map, layerIds: string[]) {
    if (!map) return;

    layerIds.forEach((layerId) => {
        if (!map.getLayer(layerId)) {
            return;
        }

        map.moveLayer(layerId);
    });
}

export type Coordinates = [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
];

export function getPointInBoundary(
    point: Point,
    fieldBoundary?: Feature<MultiPolygon>,
): Point {
    if (!fieldBoundary) return point;
    if (!turf.booleanPointInPolygon(point, fieldBoundary)) {
        const nearestPoint = turf.nearestPoint(
            point,
            turf.explode(fieldBoundary),
        );
        return nearestPoint.geometry as Point;
    }
    return point;
}
export function getPointOnBoundary(
    point: Point,
    fieldBoundary?: Feature<MultiPolygon>,
): Point {
    if (!fieldBoundary) return point;
    return turf.nearestPointOnLine(
        turf.polygonToLine(fieldBoundary) as Feature<LineString>,
        point,
        { units: "feet" },
    ).geometry as Point;
}
